import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';
import Pagination from '../components/Pagination';
import { useNotification } from '../components/Notification';
import ConfirmModal from '../components/ConfirmModal';
import { ViewIcon, DownloadIcon, SettingsIcon, SuccessIcon, OrdersIcon, CloseIcon, DeleteIcon } from '../components/Icons';
import './AdminOrders.css';

function AdminOrders() {
  const queryClient = useQueryClient();
  const { success, error: showError, warning } = useNotification();
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, onConfirm: null, message: '', title: '' });
  const [filters, setFilters] = useState({
    status: '',
    payment_status: '',
    payment_method: '',
    date_from: '',
    date_to: '',
    customer_search: '',
    order_number: '',
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkForm, setBulkForm] = useState({ status: '', payment_status: '', notes: '' });

  const { data } = useQuery({
    queryKey: ['admin-orders', filters, page, limit, sortBy, sortOrder],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit === 'all' ? '999999' : limit.toString(),
        sort: sortBy,
        order: sortOrder,
      });
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const res = await apiClient.get(`/admin/orders?${params.toString()}`);
      return res.data;
    },
  });

  // Masinių operacijų mutacijos (turi būti prieš bet kokius early returns)
  const bulkUpdateMutation = useMutation({
    mutationFn: async (data) => {
      return apiClient.put('/admin/orders/bulk', data);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries(['admin-orders']);
      setSelectedOrders(new Set());
      setShowBulkModal(false);
      setBulkForm({ status: '', payment_status: '', notes: '' });
      
      if (response.data.errors && response.data.errors.length > 0) {
        showError(`Atnaujinta ${response.data.updated.length} užsakymų. Klaidos: ${response.data.errors.length}`);
      } else {
        success(`Sėkmingai atnaujinta ${response.data.updated.length} užsakymų`);
      }
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida atliekant masines operacijas');
    },
  });

  const deleteOrderMutation = useMutation({
    mutationFn: async (order) => {
      await apiClient.post('admin/orders/delete', { id: order.id });
    },
    onSuccess: (_, order) => {
      queryClient.invalidateQueries(['admin-orders']);
      success(`Užsakymas ${order?.order_number || ''} pašalintas`);
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida šalinant užsakymą');
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (orderIds) => {
      const res = await apiClient.post('admin/orders/bulk-delete', { order_ids: orderIds });
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['admin-orders']);
      setSelectedOrders(new Set());
      if (data.errors && data.errors.length > 0) {
        showError(`Pašalinta ${data.deleted?.length || 0} užsakymų. Klaidos: ${data.errors.length}`);
      } else {
        success(`Sėkmingai pašalinta ${data.deleted?.length || 0} užsakymų`);
      }
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida šalinant užsakymus');
    },
  });

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      'pending': 'status-pending',
      'processing': 'status-processing',
      'shipped': 'status-shipped',
      'delivered': 'status-delivered',
      'cancelled': 'status-cancelled',
    };
    return classes[status] || 'status-default';
  };

  const getPaymentStatusBadgeClass = (status) => {
    const classes = {
      'pending': 'payment-pending',
      'paid': 'payment-paid',
      'failed': 'payment-failed',
      'refunded': 'payment-refunded',
    };
    return classes[status] || 'payment-default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('lt-LT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <span className="sort-icon">⇅</span>;
    return <span className="sort-icon">{sortOrder === 'ASC' ? '↑' : '↓'}</span>;
  };

  const handleExportCSV = () => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });

    const url = `/api/admin/orders/export/csv?${params.toString()}`;
    window.open(url, '_blank');
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedOrders.size === data?.orders?.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(data?.orders?.map(o => o.id) || []));
    }
  };

  const handleBulkUpdate = () => {
    if (selectedOrders.size === 0) {
      warning('Pasirinkite bent vieną užsakymą');
      return;
    }

    if (!bulkForm.status && !bulkForm.payment_status) {
      warning('Pasirinkite bent vieną statusą');
      return;
    }

    bulkUpdateMutation.mutate({
      order_ids: Array.from(selectedOrders),
      status: bulkForm.status || undefined,
      payment_status: bulkForm.payment_status || undefined,
      notes: bulkForm.notes || undefined,
    });
  };

  const handleDeleteOrder = (order) => {
    setConfirmModal({
      isOpen: true,
      title: 'Pašalinti užsakymą',
      message: `Ar tikrai norite pašalinti užsakymą ${order.order_number}? Šis veiksmas negrįžtamas.`,
      type: 'danger',
      confirmText: 'Pašalinti',
      onConfirm: () => {
        deleteOrderMutation.mutate(order, {
          onSettled: () => setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '' }),
        });
      },
    });
  };

  const handleBulkDelete = () => {
    if (selectedOrders.size === 0) {
      warning('Pasirinkite bent vieną užsakymą');
      return;
    }
    setConfirmModal({
      isOpen: true,
      title: 'Pašalinti pasirinktus užsakymus',
      message: `Ar tikrai norite pašalinti ${selectedOrders.size} užsakymų? Šis veiksmas negrįžtamas.`,
      type: 'danger',
      confirmText: 'Pašalinti visus',
      onConfirm: () => {
        bulkDeleteMutation.mutate(Array.from(selectedOrders), {
          onSettled: () => setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '' }),
        });
      },
    });
  };

  return (
    <div className="admin-orders-page">
      <div className="admin-header">
      <div className="header-title-section">
        <h1><span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}><OrdersIcon /> Užsakymų valdymas</span></h1>
        <p className="header-subtitle">Valdykite ir sekite visus užsakymus</p>
      </div>
        <div className="header-actions">
          <button onClick={handleExportCSV} className="btn btn-primary">
            <span><DownloadIcon /></span> Eksportuoti CSV
          </button>
        </div>
      </div>

      {/* Filtrai */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Statusas:</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="input"
            >
              <option value="">Visi</option>
              <option value="pending">Naujas</option>
              <option value="processing">Ruošiamas</option>
              <option value="shipped">Išsiųstas</option>
              <option value="delivered">Įvykdytas</option>
              <option value="cancelled">Atšauktas</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Mokėjimo statusas:</label>
            <select
              value={filters.payment_status}
              onChange={(e) => handleFilterChange('payment_status', e.target.value)}
              className="input"
            >
              <option value="">Visi</option>
              <option value="pending">Laukiama</option>
              <option value="paid">Apmokėta</option>
              <option value="failed">Nepavyko</option>
              <option value="refunded">Grąžinta</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Mokėjimo būdas:</label>
            <select
              value={filters.payment_method}
              onChange={(e) => handleFilterChange('payment_method', e.target.value)}
              className="input"
            >
              <option value="">Visi</option>
              <option value="card">Kortelė</option>
              <option value="cash_on_delivery">Grynieji</option>
              <option value="bank_transfer">Banko pavedimas</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Nuo datos:</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              className="input"
            />
          </div>

          <div className="filter-group">
            <label>Iki datos:</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              className="input"
            />
          </div>

          <div className="filter-group">
            <label>Klientas:</label>
            <input
              type="text"
              placeholder="Email, vardas..."
              value={filters.customer_search}
              onChange={(e) => handleFilterChange('customer_search', e.target.value)}
              className="input"
            />
          </div>

          <div className="filter-group">
            <label>Užsakymo numeris:</label>
            <input
              type="text"
              placeholder="ORD-..."
              value={filters.order_number}
              onChange={(e) => handleFilterChange('order_number', e.target.value)}
              className="input"
            />
          </div>

          <div className="filter-group">
            <button
              onClick={() => {
                setFilters({
                  status: '',
                  payment_status: '',
                  payment_method: '',
                  date_from: '',
                  date_to: '',
                  customer_search: '',
                  order_number: '',
                });
                setPage(1);
              }}
              className="btn btn-secondary"
            >
              Išvalyti
            </button>
          </div>
        </div>
      </div>

      {/* Masinių operacijų toolbar */}
      {selectedOrders.size > 0 && (
        <div className="bulk-actions-bar">
          <div className="bulk-info">
            <span>✓</span> Pasirinkta: <strong>{selectedOrders.size}</strong> užsakymų
          </div>
          <div className="bulk-buttons">
            <button
              onClick={() => setShowBulkModal(true)}
              className="btn btn-primary btn-sm"
            >
              <SettingsIcon />
              Keisti statusą ({selectedOrders.size})
            </button>
            <button
              onClick={handleBulkDelete}
              className="btn btn-danger btn-sm"
              disabled={bulkDeleteMutation.isLoading}
              title="Pašalinti pasirinktus užsakymus"
            >
              <DeleteIcon />
              Pašalinti ({selectedOrders.size})
            </button>
            <button
              onClick={() => setSelectedOrders(new Set())}
              className="btn btn-secondary btn-sm"
            >
              <CloseIcon />
              Atšaukti
            </button>
          </div>
        </div>
      )}

      {/* Užsakymų sąrašas */}
      <div className="orders-table-wrapper">
        <table className="orders-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedOrders.size > 0 && selectedOrders.size === data?.orders?.length}
                  onChange={handleSelectAll}
                  className="checkbox"
                />
              </th>
              <th onClick={() => handleSort('order_number')} className="sortable">
                Užsakymo Nr. <SortIcon field="order_number" />
              </th>
              <th onClick={() => handleSort('created_at')} className="sortable">
                Data <SortIcon field="created_at" />
              </th>
              <th>Klientas</th>
              <th onClick={() => handleSort('total')} className="sortable">
                Suma <SortIcon field="total" />
              </th>
              <th onClick={() => handleSort('status')} className="sortable">
                Statusas <SortIcon field="status" />
              </th>
              <th onClick={() => handleSort('payment_status')} className="sortable">
                Mokėjimas <SortIcon field="payment_status" />
              </th>
              <th>Prekių</th>
              <th>Veiksmai</th>
            </tr>
          </thead>
          <tbody>
            {data?.orders?.map((order) => {
              const hasActiveReturn = order.active_returns_count > 0;
              const returnNumber = order.latest_return_number;
              
              return (
              <tr 
                key={order.id} 
                className={`${selectedOrders.has(order.id) ? 'selected' : ''} ${hasActiveReturn ? 'has-return' : ''}`}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedOrders.has(order.id)}
                    onChange={() => handleSelectOrder(order.id)}
                    className="checkbox"
                    onClick={(e) => e.stopPropagation()}
                  />
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'nowrap' }}>
                    <Link to={`/admin/orders/${order.id}`} className="order-link">
                      {order.order_number}
                    </Link>
                    {hasActiveReturn && (
                      <span 
                        className="return-badge" 
                        title={`Aktyvus grąžinimas: ${returnNumber || 'N/A'}`}
                      >
                        Grąžinimas
                      </span>
                    )}
                  </div>
                </td>
                <td>{formatDate(order.created_at)}</td>
                <td>
                  {order.customer_email || (
                    <span className="guest-customer">Svečias</span>
                  )}
                  {order.customer_first_name && (
                    <div className="customer-name">
                      {order.customer_first_name} {order.customer_last_name}
                    </div>
                  )}
                </td>
                <td className="amount">{parseFloat(order.total).toFixed(2)} €</td>
                <td>
                  <span className={`status-badge ${getStatusBadgeClass(order.status)}`}>
                    {order.status === 'pending' && '○ Naujas'}
                    {order.status === 'processing' && '⚙ Ruošiamas'}
                    {order.status === 'shipped' && '→ Išsiųstas'}
                    {order.status === 'delivered' && '✓ Įvykdytas'}
                    {order.status === 'cancelled' && '× Atšauktas'}
                  </span>
                </td>
                <td>
                  <span className={`payment-badge ${getPaymentStatusBadgeClass(order.payment_status)}`}>
                    {order.payment_status === 'pending' && '○ Laukiama'}
                    {order.payment_status === 'paid' && '✓ Apmokėta'}
                    {order.payment_status === 'failed' && '× Nepavyko'}
                    {order.payment_status === 'refunded' && '↩ Grąžinta'}
                  </span>
                </td>
                <td>{order.item_count || 0}</td>
                <td>
                  <div className="row-actions">
                    <Link
                      to={`/admin/orders/${order.id}`}
                      className="btn-icon btn-icon-secondary"
                      title="Peržiūrėti užsakymą"
                    >
                      <ViewIcon />
                    </Link>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); handleDeleteOrder(order); }}
                      className="btn-icon btn-icon-danger"
                      title="Pašalinti užsakymą"
                      disabled={deleteOrderMutation.isLoading}
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>

      {/* Puslapiavimas */}
      {data?.pagination && (
        <Pagination
          page={data.pagination.page}
          totalPages={data.pagination.pages}
          totalItems={data.pagination.total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}

      {/* Masinių operacijų modalas */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2><span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}><SettingsIcon /> Masinis statusų keitimas</span></h2>
            <p className="modal-subtitle">
              Pasirinkta: <strong>{selectedOrders.size}</strong> užsakymų
            </p>
            
            <form onSubmit={(e) => { e.preventDefault(); handleBulkUpdate(); }}>
              <div className="form-group">
                <label>Užsakymo statusas:</label>
                <select
                  value={bulkForm.status}
                  onChange={(e) => setBulkForm({ ...bulkForm, status: e.target.value })}
                  className="input"
                >
                  <option value="">Nekeisti</option>
                  <option value="pending">Naujas</option>
                  <option value="processing">Ruošiamas</option>
                  <option value="shipped">Išsiųstas</option>
                  <option value="delivered">Įvykdytas</option>
                  <option value="cancelled">Atšauktas</option>
                </select>
              </div>

              <div className="form-group">
                <label>Mokėjimo statusas:</label>
                <select
                  value={bulkForm.payment_status}
                  onChange={(e) => setBulkForm({ ...bulkForm, payment_status: e.target.value })}
                  className="input"
                >
                  <option value="">Nekeisti</option>
                  <option value="pending">Laukiama</option>
                  <option value="paid">Apmokėta</option>
                  <option value="failed">Nepavyko</option>
                  <option value="refunded">Grąžinta</option>
                </select>
              </div>

              <div className="form-group">
                <label>Pastabos:</label>
                <textarea
                  value={bulkForm.notes}
                  onChange={(e) => setBulkForm({ ...bulkForm, notes: e.target.value })}
                  className="input"
                  rows="3"
                  placeholder="Pastabos apie statuso keitimą..."
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowBulkModal(false);
                    setBulkForm({ status: '', payment_status: '', notes: '' });
                  }}
                  className="btn btn-secondary"
                >
                  Atšaukti
                </button>
                <button
                  type="submit"
                  disabled={bulkUpdateMutation.isLoading}
                  className="btn btn-primary"
                >
                  {bulkUpdateMutation.isLoading ? (
                    <>○ Atnaujinama...</>
                  ) : (
                    <><SuccessIcon /> Atnaujinti</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type || 'warning'}
        confirmText={confirmModal.confirmText}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' })}
      />
    </div>
  );
}

export default AdminOrders;
